import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUIStore } from '@/store';
import { useAuth, isImpersonating } from '@/auth';
import { useTheme } from '@/theme';
import { useTranslation } from '@/i18n';
import { useTenant } from '@/tenant';
import { useBrandingLogos } from '@/api';
import { useBreakpoint, useMediaQuery } from '@/hooks';
import { ROUTES, PERMISSIONS } from '@/routes/routes';
import { SidebarAuthButtons } from './SidebarAuthButtons';
import { NavIcons } from '@/components/icons/NavIcons';
import { useUserSettings } from '@/api/hooks/useSettings';
import { USER_SETTINGS, getClientQuestionnaireId } from '@/features/settings/constants';
import { useMotrQDashboardQuestionnaires } from '@/features/dashboard/hooks';

// Sidebar-only utility icons
const Icons = {
  ChevronDown: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  Close: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
};

interface MenuItem {
  path?: string;
  labelKey: string;
  icon?: React.ReactNode;
  permission?: string | string[];
  feature?: string;
  children?: MenuItem[];
  hidden?: boolean;
  /**
   * Only show when a tenant is in context (tenant subdomain or impersonating a
   * tenant). Used for tenant-only screens like Pack Enablement that are useless
   * in the pure host context.
   */
  requiresTenant?: boolean;
  /**
   * When set, the item is active if the pathname matches ANY of these prefixes
   * (segment-aware) instead of `path`. Used where sibling menu items share a
   * base path (e.g. Home vs Client Dashboard both under /dashboard).
   */
  activeMatchPrefixes?: string[];
  /**
   * When set, the item is NOT active if the pathname matches any of these
   * prefixes (segment-aware). Lets Home stay inactive on client-workspace routes.
   */
  activeExcludePrefixes?: string[];
}

/** Segment-aware prefix match — avoids /dashboard/activity matching /dashboard/activities. */
const pathHasPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * Menu configuration matching Angular app structure
 * Uses ABP localization keys from en.json
 */
const menuConfig: MenuItem[] = [
  {
    path: ROUTES.MY_APPROVALS,
    labelKey: 'Menu:MyApprovals',
    icon: <NavIcons.MyApprovals />,
    permission: PERMISSIONS.MY_APPROVALS,
  },
  {
    labelKey: 'Menu:Add',
    icon: <NavIcons.Add />,
    permission: [PERMISSIONS.ADD.NEW_RECORD, PERMISSIONS.ADD.NEW_JOB, PERMISSIONS.ADD.NEW_SERVICE_REQUEST],
    children: [
      { path: ROUTES.ADD.NEW_RECORD, labelKey: 'AddNewRecord', permission: PERMISSIONS.ADD.NEW_RECORD },
      { path: ROUTES.ADD.NEW_JOB, labelKey: 'StartNewJob', permission: PERMISSIONS.ADD.NEW_JOB },
      { path: ROUTES.ADD.NEW_SERVICE_REQUEST, labelKey: 'Menu:StartNewServiceRequest', permission: PERMISSIONS.ADD.NEW_SERVICE_REQUEST },
    ],
  },
  {
    labelKey: 'Menu:People&Workgroups',
    icon: <NavIcons.People />,
    permission: [PERMISSIONS.PEOPLE.ORGANIZATION, PERMISSIONS.PEOPLE.USERS],
    children: [
      { path: ROUTES.PEOPLE.DIRECTORY, labelKey: 'Menu:Directory', permission: PERMISSIONS.PEOPLE.USERS },
      { path: ROUTES.PEOPLE.PEOPLE_CHANGES, labelKey: 'Menu:PeopleChanges', permission: PERMISSIONS.PEOPLE.PEOPLE_CHANGES },
      { path: ROUTES.PEOPLE.IMPORT_USERS, labelKey: 'Menu:ImportUsers', permission: PERMISSIONS.PEOPLE.IMPORT_USERS },
    ],
  },
  {
    labelKey: 'Menu:AiManagement',
    icon: <NavIcons.AI />,
    permission: [PERMISSIONS.AI.PROVIDERS, PERMISSIONS.AI.MODELS, PERMISSIONS.AI.TASKS, PERMISSIONS.AI.KNOWLEDGE_BASE, PERMISSIONS.TEMPLATES.DOCUMENT_TEMPLATES, PERMISSIONS.SKILLS.VIEW],
    feature: 'motr.PremiumFeatures.AiCapabilities.Enable',
    children: [
      { path: ROUTES.AI.PROVIDERS, labelKey: 'Menu:AiProviders', permission: PERMISSIONS.AI.PROVIDERS },
      { path: ROUTES.AI.MODELS, labelKey: 'Menu:AiModels', permission: PERMISSIONS.AI.MODELS },
      { path: ROUTES.AI.TASKS, labelKey: 'Menu:AiTasks', permission: PERMISSIONS.AI.TASKS },
      { path: ROUTES.SKILLS_LIBRARY.SKILLS_LIST, labelKey: 'Menu:Skills', permission: PERMISSIONS.SKILLS.VIEW },
      { path: ROUTES.SKILLS_LIBRARY.MCP_SERVERS_LIST, labelKey: 'Menu:McpServers', permission: PERMISSIONS.SKILLS.VIEW },
      { path: ROUTES.SKILLS_LIBRARY.PACKS_LIST, labelKey: 'Menu:Packs', permission: PERMISSIONS.SKILLS.PACKS_MANAGE },
      { path: ROUTES.SKILLS_LIBRARY.PACK_ENABLEMENT, labelKey: 'Menu:Packs', permission: PERMISSIONS.SKILLS.PACKS_ENABLE, requiresTenant: true },
      { path: ROUTES.AI.KNOWLEDGE_BASE, labelKey: 'Menu:KnowledgeBase', permission: PERMISSIONS.AI.KNOWLEDGE_BASE },
      { path: ROUTES.TEMPLATES.DOCUMENT_TEMPLATES, labelKey: 'Menu:DocumentTemplates', permission: PERMISSIONS.TEMPLATES.DOCUMENT_TEMPLATES },
    ],
  },
  {
    labelKey: 'Menu:Templates',
    icon: <NavIcons.Templates />,
    permission: [PERMISSIONS.TEMPLATES.TYPES, PERMISSIONS.TEMPLATES.TEMPLATES],
    children: [
      { path: ROUTES.TEMPLATES.PROCESS_TYPES, labelKey: 'Menu:ProcessTypes', permission: PERMISSIONS.TEMPLATES.TYPES },
      { path: ROUTES.TEMPLATES.PROCESS_TEMPLATES, labelKey: 'ProcessTemplates', permission: PERMISSIONS.TEMPLATES.TEMPLATES },
      { path: ROUTES.TEMPLATES.SNIPPETS, labelKey: 'Menu:ManageSnippets', permission: PERMISSIONS.TEMPLATES.SNIPPETS },
      { path: ROUTES.TEMPLATES.KEY_MAPS, labelKey: 'Menu:ManageKeyMaps', permission: PERMISSIONS.TEMPLATES.KEY_MAPS },
      { path: ROUTES.TEMPLATES.OUTCOMES, labelKey: 'Menu:Outcomes', permission: PERMISSIONS.TEMPLATES.OUTCOMES },
      { path: ROUTES.TEMPLATES.EMAIL_TEMPLATE_CONFIG, labelKey: 'Menu:ManageMailTemplates', permission: PERMISSIONS.TEMPLATES.EMAIL_TEMPLATES },
      { path: ROUTES.TEMPLATES.EMAIL_TEMPLATES, labelKey: 'Menu:ManageEmailTemplates', permission: PERMISSIONS.TEMPLATES.EMAIL_TEMPLATES },
      { path: ROUTES.TEMPLATES.TAGS, labelKey: 'Menu:Tags', permission: [PERMISSIONS.TEMPLATES.TAGS, PERMISSIONS.TEMPLATES.TAG_CATEGORIES] },
      { path: ROUTES.TEMPLATES.DATA_FORMS, labelKey: 'Menu:DataForms', permission: PERMISSIONS.TEMPLATES.DATA_FORMS },
    ],
  },
  {
    labelKey: 'Menu:Reports',
    icon: <NavIcons.Reports />,
    permission: [PERMISSIONS.REPORTS.USERS, PERMISSIONS.REPORTS.JOB_HISTORY, PERMISSIONS.REPORTS.PERFORMANCE, PERMISSIONS.REPORTS.PIPELINE, PERMISSIONS.REPORTS.AI_USAGE_VIEW_ALL, PERMISSIONS.REPORTS.AI_USAGE_VIEW_OWN, PERMISSIONS.REPORTS.AI_ACTIVITY_VIEW, PERMISSIONS.REPORTS.JOB_STEP_APPROVALS, PERMISSIONS.REPORTS.JOB_STEP_BASED_APPROVAL, PERMISSIONS.REPORTS.RISKS, PERMISSIONS.REPORTS.ACTIVITY],
    children: [
      { path: ROUTES.REPORTS.POWER_BI, labelKey: 'Menu:PowerBiReports', permission: PERMISSIONS.REPORTS.POWER_BI },
      { path: ROUTES.REPORTS.USERS, labelKey: 'Menu:Users', permission: PERMISSIONS.REPORTS.USERS },
      { path: ROUTES.REPORTS.ACTIVITY, labelKey: 'Menu:ActivityReport', permission: PERMISSIONS.REPORTS.ACTIVITY },
      { path: ROUTES.REPORTS.JOB_AUDIT, labelKey: 'Menu:JobAudit', permission: PERMISSIONS.REPORTS.JOB_HISTORY },
      { path: ROUTES.REPORTS.JOB_TIME_LOGS, labelKey: 'Menu:JobTimeLogs', permission: PERMISSIONS.REPORTS.PERFORMANCE },
      { path: ROUTES.REPORTS.PERFORMANCE, labelKey: 'Menu:SystemPerformance', permission: PERMISSIONS.REPORTS.PERFORMANCE },
      { path: ROUTES.REPORTS.TAGS_PERFORMANCE, labelKey: 'Menu:TagsPerformance', permission: PERMISSIONS.REPORTS.PERFORMANCE },
      { path: ROUTES.REPORTS.PIPELINE, labelKey: 'Menu:Pipeline', permission: PERMISSIONS.REPORTS.PIPELINE },
      { path: ROUTES.REPORTS.WORKGROUP_PIPELINE, labelKey: 'Menu:WorkgroupPipeline', permission: PERMISSIONS.REPORTS.WORKGROUP_PIPELINE },
      { path: ROUTES.REPORTS.TEMPLATE_AUDIT, labelKey: 'Menu:TemplateAudit', permission: PERMISSIONS.REPORTS.TEMPLATE_AUDIT },
      { path: ROUTES.REPORTS.ND_RECORD_AUDIT, labelKey: 'Menu:NDRecordAudit', permission: PERMISSIONS.REPORTS.ND_RECORD_AUDIT },
      { path: ROUTES.REPORTS.JOB_OUTCOME, labelKey: 'Menu:JobOutcome', permission: PERMISSIONS.REPORTS.JOB_OUTCOMES },
      { path: ROUTES.REPORTS.DURATION, labelKey: 'Menu:Duration', permission: PERMISSIONS.REPORTS.DURATION },
      { path: ROUTES.REPORTS.STEP_DURATION, labelKey: 'Menu:StepDuration', permission: PERMISSIONS.REPORTS.DURATION },
      { path: ROUTES.REPORTS.TIME_BREAKDOWN, labelKey: 'Menu:TimeBreakdown', permission: PERMISSIONS.REPORTS.TIME_BREAKDOWN },
      { path: ROUTES.REPORTS.TAGS, labelKey: 'Menu:TagsReport', permission: PERMISSIONS.REPORTS.TAGS },
      { path: ROUTES.REPORTS.SUGGESTED_IMPROVEMENTS, labelKey: 'Menu:SuggestedImprovements', permission: PERMISSIONS.REPORTS.SUGGESTED_IMPROVEMENTS },
      { path: ROUTES.REPORTS.CLIENT_FEEDBACK, labelKey: 'Menu:ClientFeedback', permission: PERMISSIONS.REPORTS.SUGGESTED_IMPROVEMENTS },
      { path: ROUTES.REPORTS.RISKS, labelKey: 'Menu:Risks', permission: PERMISSIONS.REPORTS.RISKS },
      { path: ROUTES.REPORTS.AI_USAGE, labelKey: 'Menu:AiUsage', permission: [PERMISSIONS.REPORTS.AI_USAGE_VIEW_ALL, PERMISSIONS.REPORTS.AI_USAGE_VIEW_OWN] },
      { path: ROUTES.REPORTS.AI_ACTIVITY, labelKey: 'Menu:AiActivity', permission: PERMISSIONS.REPORTS.AI_ACTIVITY_VIEW },
      { path: ROUTES.REPORTS.JOB_STEP_APPROVALS, labelKey: 'Menu:JobStepApprovals', permission: PERMISSIONS.REPORTS.JOB_STEP_APPROVALS },
      { path: ROUTES.REPORTS.JOB_STEP_BASED_APPROVAL, labelKey: 'Menu:JobStepBasedApproval', permission: PERMISSIONS.REPORTS.JOB_STEP_BASED_APPROVAL },
    ],
  },
  {
    labelKey: 'Menu:Administration',
    icon: <NavIcons.Admin />,
    permission: [PERMISSIONS.SETTINGS.GENERAL, PERMISSIONS.ADMIN.SAAS, PERMISSIONS.ADMIN.ROLES, PERMISSIONS.ADMIN.LANGUAGE_TEXTS, PERMISSIONS.ADMIN.ENTITY_TYPES, PERMISSIONS.ADMIN.ENTITY_RECORDS],
    children: [
      { path: ROUTES.SETTINGS.ROOT, labelKey: 'Settings', permission: PERMISSIONS.SETTINGS.GENERAL },
      { path: ROUTES.ADMIN.TENANTS, labelKey: 'Menu:Tenants', permission: PERMISSIONS.ADMIN.SAAS },
      { path: ROUTES.ADMIN.ROLES, labelKey: 'Menu:Roles', permission: PERMISSIONS.ADMIN.ROLES },
      { path: ROUTES.ADMIN.ENTITY_TYPES, labelKey: 'Menu:EntityTypes', permission: PERMISSIONS.ADMIN.ENTITY_TYPES },
      { path: ROUTES.ADMIN.ENTITY_RECORDS, labelKey: 'Menu:EntityRecords', permission: PERMISSIONS.ADMIN.ENTITY_RECORDS },
      { path: ROUTES.ADMIN.LANGUAGE_TEXTS, labelKey: 'Menu:LanguageText', permission: PERMISSIONS.ADMIN.LANGUAGE_TEXTS },
    ],
  },
];

interface NavItemProps {
  item: MenuItem;
  collapsed: boolean;
  depth?: number;
  onNavigate?: () => void;
}

const NavItem = ({ item, collapsed, depth = 0, onNavigate }: NavItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const { hasPermission, hasFeature, featuresLoaded } = useAuth();
  const { isHost } = useTenant();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();

  // Tenant context exists on a tenant subdomain or while impersonating a tenant
  // from the host. Tenant-only items (requiresTenant) are hidden in pure host.
  const hasTenantContext = !isHost || isImpersonating();
  const flyoutRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close flyout on outside click
  useEffect(() => {
    if (!flyoutOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        flyoutRef.current && !flyoutRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setFlyoutOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [flyoutOpen]);

  // Adjust flyout position to stay within viewport
  useEffect(() => {
    if (!flyoutOpen || !flyoutRef.current) return;
    const el = flyoutRef.current;
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const padding = 8;
    if (rect.bottom > viewportHeight - padding) {
      setFlyoutPos((prev) => ({
        ...prev,
        top: Math.max(padding, prev.top - (rect.bottom - viewportHeight + padding)),
      }));
    }
  }, [flyoutOpen]);

  // Close flyout on route change
  useEffect(() => {
    setFlyoutOpen(false);
  }, [location.pathname]);

  // Check if user has any of the required permissions and features
  const hasRequiredPermission = () => {
    // Don't hide feature-gated items until features are actually loaded
    if (item.feature && featuresLoaded && !hasFeature(item.feature)) return false;
    if (!item.permission) return true;
    if (Array.isArray(item.permission)) {
      return item.permission.some((p) => hasPermission(p));
    }
    return hasPermission(item.permission);
  };

  if (!hasRequiredPermission() || item.hidden) return null;
  if (item.requiresTenant && !hasTenantContext) return null;

  const hasChildren = item.children && item.children.length > 0;
  let isActive: boolean;
  if (item.path) {
    if (item.activeMatchPrefixes || item.activeExcludePrefixes) {
      const prefixes = item.activeMatchPrefixes ?? [item.path];
      const matched = prefixes.some((p) => pathHasPrefix(location.pathname, p));
      const excluded = item.activeExcludePrefixes?.some((p) => pathHasPrefix(location.pathname, p)) ?? false;
      isActive = matched && !excluded;
    } else {
      isActive = item.path === ROUTES.HOME
        ? location.pathname === item.path
        : location.pathname.startsWith(item.path);
    }
  } else {
    isActive = !!item.children?.some((child) => child.path && location.pathname.startsWith(child.path));
  }

  const label = t(item.labelKey);
  const paddingLeft = collapsed ? 12 : 12 + depth * 16;

  // Filter visible children
  const visibleChildren = item.children?.filter((child) => {
    if (child.hidden) return false;
    if (child.requiresTenant && !hasTenantContext) return false;
    if (!child.permission) return true;
    if (Array.isArray(child.permission)) {
      return child.permission.some((p) => hasPermission(p));
    }
    return hasPermission(child.permission);
  });

  if (hasChildren && (!visibleChildren || visibleChildren.length === 0)) {
    return null;
  }

  const handleClick = () => {
    if (hasChildren) {
      if (collapsed) {
        if (!flyoutOpen && buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setFlyoutPos({ top: rect.top, left: rect.right + 4 });
        }
        setFlyoutOpen(!flyoutOpen);
      } else {
        setIsOpen(!isOpen);
      }
    }
  };

  const handleNavClick = () => {
    onNavigate?.();
  };

  // Determine colors based on active state
  const textColor = isActive ? theme.sidebar.activeText : theme.sidebar.textMuted;
  const iconColor = isActive ? theme.sidebar.iconActiveColor : theme.sidebar.iconColor;

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    padding: collapsed ? `${theme.spacing.md} ${theme.spacing.md}` : `${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md} ${paddingLeft}px`,
    marginBottom: '2px',
    borderRadius: theme.borderRadius.md,
    color: textColor,
    backgroundColor: isActive && !hasChildren ? theme.sidebar.activeBackground : 'transparent',
    textDecoration: 'none',
    fontSize: depth === 0 ? theme.sidebar.fontSizeParent : theme.sidebar.fontSizeChild,
    fontWeight: isActive ? theme.typography.fontWeightSemibold : theme.typography.fontWeightNormal,
    justifyContent: collapsed ? 'center' : 'flex-start',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    transition: `all ${theme.transitions.default} ease`,
  };

  const content = (
    <>
      {/* Icon with explicit color */}
      <span style={{ color: iconColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {item.icon}
      </span>
      {!collapsed && (
        <>
          <span style={{ flex: 1, color: textColor }}>{label}</span>
          {hasChildren && (
            <span
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: `transform ${theme.transitions.default} ease`,
                color: iconColor,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Icons.ChevronDown />
            </span>
          )}
        </>
      )}
    </>
  );

  return (
    <div>
      {item.path && !hasChildren ? (
        <NavLink to={item.path} style={itemStyle} title={collapsed ? label : undefined} onClick={handleNavClick}>
          {content}
        </NavLink>
      ) : (
        <button ref={buttonRef} onClick={handleClick} style={itemStyle} title={collapsed ? label : undefined}>
          {content}
        </button>
      )}

      {/* Expanded inline children */}
      {hasChildren && !collapsed && (
        <div
          style={{
            maxHeight: isOpen ? `${(visibleChildren?.length || 0) * 48}px` : '0',
            overflow: 'hidden',
            transition: `max-height ${theme.transitions.default} ease`,
          }}
        >
          {visibleChildren?.map((child, index) => (
            <NavItem key={child.path || index} item={child} collapsed={collapsed} depth={depth + 1} onNavigate={onNavigate} />
          ))}
        </div>
      )}

      {/* Collapsed flyout popover — uses fixed positioning to escape sidebar overflow */}
      {hasChildren && collapsed && flyoutOpen && (
        <div
          ref={flyoutRef}
          style={{
            position: 'fixed',
            top: flyoutPos.top,
            left: flyoutPos.left,
            backgroundColor: theme.sidebar.background,
            border: `1px solid ${theme.sidebar.border}`,
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.dropdown,
            padding: theme.spacing.sm,
            minWidth: '200px',
            maxHeight: 'calc(100vh - 16px)',
            overflowY: 'auto',
            zIndex: 300,
          }}
        >
          {/* Flyout header */}
          <div
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              color: theme.sidebar.text,
              fontSize: theme.sidebar.fontSizeChild,
              fontWeight: theme.typography.fontWeightSemibold,
              borderBottom: `1px solid ${theme.sidebar.border}`,
              marginBottom: theme.spacing.xs,
            }}
          >
            {label}
          </div>
          {visibleChildren?.map((child) => {
            const childLabel = t(child.labelKey);
            const childIsActive = child.path && location.pathname.startsWith(child.path);
            return (
              <NavLink
                key={child.path}
                to={child.path || '#'}
                onClick={() => {
                  setFlyoutOpen(false);
                  onNavigate?.();
                }}
                style={{
                  display: 'block',
                  padding: `${theme.spacing.md} ${theme.spacing.md}`,
                  borderRadius: theme.borderRadius.md,
                  color: childIsActive ? theme.sidebar.activeText : theme.sidebar.textMuted,
                  backgroundColor: childIsActive ? theme.sidebar.activeBackground : 'transparent',
                  textDecoration: 'none',
                  fontSize: theme.sidebar.fontSizeChild,
                  fontWeight: childIsActive ? theme.typography.fontWeightSemibold : theme.typography.fontWeightNormal,
                  transition: `all ${theme.transitions.default} ease`,
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!childIsActive) e.currentTarget.style.backgroundColor = theme.sidebar.hoverBackground;
                }}
                onMouseLeave={(e) => {
                  if (!childIsActive) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {childLabel}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Sidebar widths
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 60;

/**
 * Client-workspace tabs, in display order. Each has its own child permission.
 * The Client Dashboard menu entry is shown only when the user holds at least
 * one of these, and it lands on the first tab they're permitted to see.
 */
const CLIENT_DASHBOARD_TABS: { path: string; permission: string }[] = [
  { path: ROUTES.CLIENT.REQUESTS, permission: PERMISSIONS.CLIENT_DASHBOARD.MY_REQUESTS },
  { path: ROUTES.CLIENT.FRONT_DOOR, permission: PERMISSIONS.CLIENT_DASHBOARD.SERVICE_REQUESTS },
  { path: ROUTES.CLIENT.DOCUMENTS, permission: PERMISSIONS.CLIENT_DASHBOARD.DOCUMENTS },
  { path: ROUTES.CLIENT.ACTIVITY, permission: PERMISSIONS.CLIENT_DASHBOARD.ACTIVITY },
];

/** Route prefixes for the Client Dashboard — drive the entry's active-state. */
const CLIENT_DASHBOARD_PREFIXES = CLIENT_DASHBOARD_TABS.map((t) => t.path);

const CLIENT_LANDING_PAGE_ROUTE_MAP: Record<string, string> = {
  'front-door': ROUTES.CLIENT.FRONT_DOOR,
  requests: ROUTES.CLIENT.REQUESTS,
  documents: ROUTES.CLIENT.DOCUMENTS,
  activity: ROUTES.CLIENT.ACTIVITY,
};

/**
 * Internal-Dashboard tab permissions. Holding any of these means the user has a
 * real internal Dashboard to land on. NOTE: the generic "Home" permission
 * (motr.Dashboard.HomePage) is deliberately NOT here — it's a label permission
 * everyone gets, not actual Dashboard access.
 */
const INTERNAL_DASHBOARD_PERMISSIONS = [
  PERMISSIONS.DASHBOARD.WIP,
  PERMISSIONS.DASHBOARD.CALENDAR,
  PERMISSIONS.DASHBOARD.ACTIVITIES,
  PERMISSIONS.DASHBOARD.WORKLOAD,
  PERMISSIONS.DASHBOARD.SUMMARY,
];

/**
 * Build the top-of-menu dashboard entries from the user's permissions:
 * - Dashboard + Client Dashboard → "Home" (internal) + "Client Dashboard".
 * - Only one → shown as "Home".
 * The Client Dashboard entry is omitted entirely when the user has no client
 * child permission; `clientLandingPath` is its first accessible tab (or null).
 */
const buildDashboardMenuItems = (
  hasDashboard: boolean,
  clientLandingPath: string | null,
): MenuItem[] => {
  const homeItem: MenuItem = {
    path: ROUTES.DASHBOARD.ROOT,
    labelKey: 'Menu:Home',
    icon: <NavIcons.Home />,
    // Gated by hasDashboard (real internal-Dashboard access), computed by caller.
    // Stay inactive while on a client-workspace route (shared /dashboard base).
    activeExcludePrefixes: CLIENT_DASHBOARD_PREFIXES,
  };
  const clientItem: MenuItem | null = clientLandingPath
    ? {
        path: clientLandingPath,
        labelKey: hasDashboard ? 'Menu:ClientDashboard' : 'Menu:Home',
        icon: hasDashboard ? <NavIcons.Dashboard /> : <NavIcons.Home />,
        activeMatchPrefixes: CLIENT_DASHBOARD_PREFIXES,
      }
    : null;

  if (hasDashboard && clientItem) return [homeItem, clientItem];
  if (clientItem) return [clientItem];
  if (hasDashboard) return [homeItem];
  return [];
};

export const Sidebar = () => {
  const { sidebarCollapsed, toggleSidebar, mobileMenuOpen, setMobileMenuOpen } = useUIStore();
  const { hasPermission } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { settings, companyName, isLoading: isTenantLoading } = useTenant();
  const { data: brandingLogos, isLoading: isBrandingLoading } = useBrandingLogos();
  const { isMobile, isTablet } = useBreakpoint();
  const location = useLocation();
  const { data: clientLandingSettings } = useUserSettings(USER_SETTINGS.CLIENT_DASHBOARD_LANDING_PAGE);

  // Small desktop: 1024-1280px - auto-collapse sidebar for more content space
  const isSmallDesktop = useMediaQuery('(min-width: 1024px) and (max-width: 1279px)');

  const displayName = companyName || 'mot-r';

  // Dynamic dashboard entries: Home (internal) and/or Client Dashboard.
  // "Home" appears only with real internal-Dashboard access (not just the
  // generic HomePage permission); Client Dashboard only with ≥1 client child.
  // When the user has only client access, the single entry is labelled "Home".
  const hasInternalDashboard = INTERNAL_DASHBOARD_PERMISSIONS.some((p) => hasPermission(p));
  const savedClientLandingValue = clientLandingSettings?.find(
    (setting) => setting.name === USER_SETTINGS.CLIENT_DASHBOARD_LANDING_PAGE,
  )?.value || 'requests';
  const savedQuestionnaireId = getClientQuestionnaireId(savedClientLandingValue);
  const canOpenClientQuestionnaire =
    hasPermission(PERMISSIONS.CLIENT_DASHBOARD.SERVICE_REQUESTS)
    && hasPermission(PERMISSIONS.MOTRQ.SUBMIT_QUESTIONNAIRE);
  const { questionnaires: clientDashboardQuestionnaires } = useMotrQDashboardQuestionnaires(
    Boolean(savedQuestionnaireId) && canOpenClientQuestionnaire,
  );
  const savedQuestionnaireIsAvailable =
    !savedQuestionnaireId
    || clientDashboardQuestionnaires.some(
      (questionnaire) => questionnaire.questionnaireId === savedQuestionnaireId,
    );
  const savedClientLandingPath = savedQuestionnaireId
    ? `${ROUTES.CLIENT.FRONT_DOOR}/${encodeURIComponent(savedQuestionnaireId)}`
    : CLIENT_LANDING_PAGE_ROUTE_MAP[savedClientLandingValue];
  const savedClientLandingBasePath = savedQuestionnaireId
    ? ROUTES.CLIENT.FRONT_DOOR
    : savedClientLandingPath;
  const canAccessSavedClientLanding = CLIENT_DASHBOARD_TABS.some(
    (tab) => tab.path === savedClientLandingBasePath && hasPermission(tab.permission),
  ) && savedQuestionnaireIsAvailable;
  const clientLandingPath =
    (canAccessSavedClientLanding ? savedClientLandingPath : null)
    ?? CLIENT_DASHBOARD_TABS.find((tab) => hasPermission(tab.permission))?.path
    ?? null;
  const dashboardMenuItems = buildDashboardMenuItems(hasInternalDashboard, clientLandingPath);

  // Close mobile menu on route change
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen, setMobileMenuOpen]);

  // Determine if sidebar should be shown as overlay (mobile) or fixed (desktop).
  // On small desktop (1024-1279) the sidebar is normally a fixed 60px icon bar,
  // but when the user explicitly opens the menu it appears as a full overlay
  // so they can see the labels — a plain toggleSidebar has no effect here
  // because isSmallDesktop pins isCollapsed to true.
  const isOverlay = isMobile || isTablet || (isSmallDesktop && mobileMenuOpen);
  const isVisible = isOverlay ? mobileMenuOpen : true;
  const isCollapsed = isOverlay ? false : (isSmallDesktop || sidebarCollapsed);

  const handleCloseMobile = () => {
    setMobileMenuOpen(false);
  };

  const handleNavigate = () => {
    if (isOverlay) {
      setMobileMenuOpen(false);
    }
  };

  // Don't render anything on mobile when closed (for performance)
  if (isOverlay && !isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop for mobile/tablet */}
      {isOverlay && isVisible && (
        <div
          onClick={handleCloseMobile}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: theme.colors.overlayBackdrop,
            zIndex: 199,
            opacity: isVisible ? 1 : 0,
            transition: `opacity ${theme.transitions.default} ease`,
          }}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: isOverlay ? SIDEBAR_WIDTH_EXPANDED : (isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED),
          backgroundColor: theme.sidebar.background,
          transition: `transform ${theme.transitions.default} ease, width ${theme.transitions.default} ease`,
          transform: isOverlay ? (isVisible ? 'translateX(0)' : `translateX(-100%)`) : 'translateX(0)',
          zIndex: 200,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isOverlay ? theme.shadows.dropdown : 'none',
        }}
      >
        {/* Header - only show when expanded or in overlay mode */}
        {(!isCollapsed || isOverlay) && (
          <div
            style={{
              height: '56px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `0 ${theme.spacing.lg}`,
              borderBottom: `1px solid ${theme.sidebar.border}`,
              flexShrink: 0,
            }}
          >
            {(() => {
              // Logo priority: theme overrides > branding API logos (auth-aware) > tenant settings logos > blob storage logoUrl > text
              // sidebarMode='dark' → use light/white logo; sidebarMode='light' → use dark/original logo
              const lightLogo = theme.sidebar.logoLightUrl || brandingLogos?.lightLogoUrl || settings.logoLightUrl;
              const darkLogo = theme.sidebar.logoDarkUrl || brandingLogos?.darkLogoUrl || settings.logoDarkUrl;
              if (lightLogo || darkLogo) {
                const logoSrc = theme.sidebar.sidebarMode === 'dark'
                  ? (lightLogo || darkLogo)
                  : (darkLogo || lightLogo);
                if (logoSrc) {
                  return <img src={logoSrc} alt={displayName} style={{ height: '28px', maxWidth: '180px', objectFit: 'contain' }} />;
                }
              }
              // While branding logos are still loading, show placeholder instead of blob storage fallback
              if (isBrandingLoading || isTenantLoading) {
                return <span style={{ height: '28px', display: 'inline-block' }} />;
              }
              if (settings.logoUrl) {
                return (
                  <img
                    src={settings.logoUrl}
                    alt={displayName}
                    style={{
                      height: '28px',
                      maxWidth: '180px',
                      objectFit: 'contain',
                      filter: theme.sidebar.sidebarMode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                    }}
                  />
                );
              }
              return (
                <span
                  style={{
                    color: theme.sidebar.text,
                    fontSize: theme.sidebar.fontSizeHeader,
                    fontWeight: theme.typography.fontWeightBold,
                    letterSpacing: '1px',
                  }}
                >
                  {displayName}
                </span>
              );
            })()}

            {/* Close button for mobile, collapse button for desktop */}
            <button
              onClick={isOverlay ? handleCloseMobile : toggleSidebar}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: theme.spacing.sm,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={isOverlay ? t('Close') : t('Collapse')}
              aria-label={isOverlay ? t('Sidebar:CloseMenu') : t('Sidebar:CollapseSidebar')}
            >
              <span style={{ color: theme.sidebar.iconColor, display: 'flex' }}>
                {isOverlay ? <Icons.Close /> : <Icons.ChevronLeft />}
              </span>
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav
          style={{
            flex: 1,
            padding: `${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.xxl}`,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {dashboardMenuItems.map((item, index) => (
            <NavItem key={item.path || `dash-${index}`} item={item} collapsed={isCollapsed} onNavigate={handleNavigate} />
          ))}
          {menuConfig.map((item, index) => (
            <NavItem key={item.path || index} item={item} collapsed={isCollapsed} onNavigate={handleNavigate} />
          ))}
        </nav>

        {/* NetDocuments & DocuSign auth buttons at bottom */}
        <SidebarAuthButtons collapsed={isCollapsed} />
      </aside>
    </>
  );
};

// Export sidebar dimensions for use in other components
export { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED };
